import { Component, input, output, computed, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PricingDisplayProduct, PricingDisplayRegion } from '../../models';
import { PricingStoreService } from '../../services/pricing-store.service';
// Import sibling component directly to avoid circular dependency via barrel
import { PlanDisplayComponent } from '../plan-display/plan-display.component';
import { ResponsiveService } from '../../services/responsive.service';

@Component({
  selector: 'comparison-modal',
  standalone: true,
  imports: [CommonModule, PlanDisplayComponent],
  templateUrl: './comparison-modal.component.html',
  // styleUrls: ['./comparison-modal.component.scss']
})
/**
 * ComparisonModalComponent
 * Displays a full-feature comparison grid inside a modal overlay.
 * Visibility is controlled by the parent component via structural directive (@if).
 * Reads product data directly from store to avoid initialization race conditions.
 */
export class ComparisonModalComponent {
  private readonly store = inject(PricingStoreService);
  private readonly responsive = inject(ResponsiveService);

  // Unique key to force recreation on each open
  readonly modalKey = input<number>(0);
  
  // Available regions passed from parent
  readonly availableRegions = input<PricingDisplayRegion[]>([]);

  // Read directly from store - no inputs needed!
  readonly product = computed(() => this.store.selectedProduct());
  
  // Signal-based outputs
  readonly onClose = output<void>();
  readonly onPlanSelect = output<any>();
  readonly onRegionChange = output<PricingDisplayRegion>();
  readonly onRangedValueChange = output<number>(); 

  // Computed properties - now reactive to store signals!
  readonly hasProduct = computed(() => !!this.product());
  readonly productName = computed(() => this.product()?.name || '');
  readonly productDescription = computed(() => this.product()?.description || '');

  /** Reactive mobile/tablet/desktop detection */
  readonly isMobile = this.responsive.isMobile;
  readonly isTablet = this.responsive.isTablet;
  readonly isDesktop = this.responsive.isDesktop;
  readonly isMobileOrTablet = this.responsive.isMobileOrTablet;

  /**
   * Close the modal and emit close event.
   */
  close() {
    this.onClose.emit();
  }

  /**
   * Handle plan selection from nested component, then close the modal.
   */
  onPlanSelected(plan: any) {
    this.onPlanSelect.emit(plan);
    this.close(); // Close modal after plan selection
  }

  /**
   * Close when clicking on the backdrop outside modal content.
   */
  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  /**
   * Close when pressing Escape.
   */
  @HostListener('keydown.escape')
  onEscapeKey() {
    this.close();
  }
}
